import { memo, useCallback } from "react";
import { useShallow } from "zustand/react/shallow";

import TableHead from "ds/components/Table/Head";
import { getHeadColumnKey } from "ds/components/Table/Head/helpers";
import { selectVisibleColumns } from "ds/components/Table/store/useTableStore/selectors";

import { TableHeadInteractiveColumnProps } from "../Table/Head/InteractiveColumn";
import TableSettings from "../Table/Settings";
import { TableSettingsDrawerProps } from "../Table/Settings/DrawerContent";
import { SelectAllOption } from "../Table/types";
import TableSimpleHeadColumn from "./HeadColumn";
import { isStickyLeft, isStickyRight } from "./helpers";
import useTableSimpleEffectiveColumnPinning from "./hooks/useEffectiveColumnPinning";
import { showTableSimpleSettingsDrawer } from "./SettingsDrawer";
import { TableSimpleSelectionStore, TableSimpleStore } from "./types";

type TableSimpleHeadProps = {
  store: TableSimpleStore;
  selectionStore?: TableSimpleSelectionStore;
  selectionOptions?: SelectAllOption[];
  ariaLabel: string;
} & Pick<TableSettingsDrawerProps, "trackAnalytics"> &
  Pick<
    TableHeadInteractiveColumnProps,
    | "analyticsPage"
    | "filtersStorageKey"
    | "sortCallback"
    | "tableRef"
    | "sortConfig"
    | "externalSortManagement"
  >;

const TableSimpleHead = ({
  store,
  selectionStore,
  selectionOptions,
  ariaLabel,
  sortCallback,
  analyticsPage,
  filtersStorageKey,
  trackAnalytics,
  tableRef,
  sortConfig,
  externalSortManagement,
}: TableSimpleHeadProps) => {
  const { columns, isFirstColumnPinned } = store(
    useShallow((state) => ({
      columns: selectVisibleColumns(state),
      isFirstColumnPinned: state.isFirstColumnPinned,
    }))
  );

  const { effectiveIsFirstColumnPinned, isPinningEnabled } = useTableSimpleEffectiveColumnPinning(
    tableRef,
    isFirstColumnPinned
  );

  const showTableSettingsDrawer = useCallback(() => {
    showTableSimpleSettingsDrawer({
      store,
      trackAnalytics,
    });
  }, [store, trackAnalytics]);

  return (
    <TableHead>
      {columns.map((column, i) => {
        const isFirstColumn = i === 0;
        const isLastColumn = i === columns.length - 1;
        const withSelection = selectionStore && isFirstColumn && !!selectionOptions?.length;

        return (
          <TableSimpleHeadColumn
            key={getHeadColumnKey(column)}
            column={column}
            store={store}
            {...(withSelection && {
              selectionStore,
              selectionOptions,
            })}
            stickyLeft={isStickyLeft(i, effectiveIsFirstColumnPinned)}
            stickyRight={isStickyRight(i, columns.length)}
            analyticsPage={analyticsPage}
            filtersStorageKey={filtersStorageKey}
            sortCallback={sortCallback}
            tableRef={tableRef}
            sortConfig={sortConfig}
            isLast={isLastColumn}
            externalSortManagement={externalSortManagement}
            isPinningEnabled={isPinningEnabled}
          >
            {isLastColumn && (
              <TableSettings title={`${ariaLabel} settings`} onPress={showTableSettingsDrawer} />
            )}
          </TableSimpleHeadColumn>
        );
      })}
    </TableHead>
  );
};

TableSimpleHead.displayName = "DS.TableSimpleHead";

export default memo(TableSimpleHead) as typeof TableSimpleHead;
